/**
 * Generiert ein standardisiertes HTML-Footer für E-Mails
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers
 * @returns {string} HTML-Code für den E-Mail-Footer
 */
export const generateEmailFooter = (userSettings) => {
  console.log(`🦶 [FOOTER-GENERATOR] generateEmailFooter called with:`, {
    hasUserSettings: !!userSettings,
    userSettingsType: typeof userSettings,
    userSettingsKeys: userSettings ? Object.keys(userSettings) : [],
    footerText: userSettings?.footer_text,
    footerImageUrl: userSettings?.footer_image_url,
    footerImagePosition: userSettings?.footer_image_position,
    footerFont: userSettings?.footer_font,
    footerImageSize: userSettings?.footer_image_size
  });

  // Prüfe, ob die notwendigen Einstellungen vorhanden sind
  if (!userSettings) {
    console.warn('🦶 [FOOTER-GENERATOR] No user settings found for footer');
    return '';
  }
  
  // Prüfe, ob das Bild neben dem Text angezeigt werden soll
  const isBesideLayout = userSettings.footer_image_position === 'beside' && userSettings.footer_image_url;
  
  // Bildgröße aus den Einstellungen oder Standardwert
  const imageSize = userSettings.footer_image_size || 80;
  
  if (isBesideLayout) {
    // Layout mit Bild neben dem Text (mit DIVs)
    // Verwende table-basiertes Layout für maximale Kompatibilität
    return `
      <!-- FOOTER -->
      <div style="margin-top: 15px; max-width: 500px; font-family: '${userSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; color: #666; font-size: 12px; line-height: 1.5; text-align: left;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="table-layout: fixed; text-align: left;">
          <tr>
            <td width="${imageSize}px" valign="middle" align="left" style="width: ${imageSize}px; padding-right: 15px; vertical-align: middle;">
              <img src="${userSettings.footer_image_url}" alt="Logo" style="display: block; max-width: 100%; max-height: ${imageSize}px; width: auto; height: auto; object-fit: contain; margin: 0; padding: 0;" />
            </td>
            <td valign="middle" align="left" style="text-align: left; vertical-align: middle;">
              ${userSettings.footer_text ? 
                `<div style="margin: 0; font-family: '${userSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #666; text-align: left;">
                  ${userSettings.footer_text}
                </div>` : 
                ''}
            </td>
          </tr>
        </table>
      </div>
    `;
  } else {
    // Standard-Layout mit Bild unter dem Text
    return `
      <!-- FOOTER -->
      <div style="margin-top: 0px; padding-top: 0px; max-width: 500px; font-family: '${userSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; color: #666; font-size: 12px; line-height: 1.5; text-align: left;">
        ${userSettings.footer_text ? 
          `<div style="margin: 0; font-family: '${userSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #666; text-align: left;">
            ${userSettings.footer_text}
          </div>` : 
          ''}
        ${userSettings.footer_image_url ? 
          `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 10px; padding-top: 5px; table-layout: fixed; text-align: left;">
            <tr>
              <td align="left" style="text-align: left;">
                <img src="${userSettings.footer_image_url}" alt="Logo" style="display: block; max-width: 100%; max-height: ${imageSize}px; width: auto; height: auto; object-fit: contain; margin: 0; padding: 0;" />
              </td>
            </tr>
          </table>` : 
          ''}
      </div>
    `;
  }
};

/**
 * Fügt einen Footer zu einem E-Mail-Inhalt hinzu
 * @param {string} emailContent - Der E-Mail-Inhalt
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers
 * @returns {string} E-Mail-Inhalt mit Footer
 */
export const addFooterToEmail = (emailContent, userSettings = {}) => {
  console.log(`🦶 [FOOTER-GENERATOR] addFooterToEmail called with:`, {
    hasEmailContent: !!emailContent,
    emailContentLength: emailContent?.length,
    hasUserSettings: !!userSettings,
    userSettingsKeys: Object.keys(userSettings || {}),
    footerText: userSettings?.footer_text,
    footerImageUrl: userSettings?.footer_image_url,
    footerFont: userSettings?.footer_font,
    footerImagePosition: userSettings?.footer_image_position,
    footerImageSize: userSettings?.footer_image_size
  });

  // Prüfe, ob der Inhalt bereits einen Footer hat
  if (emailContent.includes('<!-- FOOTER -->')) {
    console.log(`🦶 [FOOTER-GENERATOR] Footer already exists, returning original content`);
    return emailContent; // Footer bereits vorhanden
  }
  
  // Verwende die generateEmailFooter-Funktion, um den komplexen Footer zu erstellen
  const footerHtml = generateEmailFooter(userSettings);
  
  console.log(`🦶 [FOOTER-GENERATOR] Generated footer HTML:`, {
    hasFooterHtml: !!footerHtml,
    footerHtmlLength: footerHtml?.length,
    footerHtmlPreview: footerHtml?.substring(0, 200)
  });
  
  // Wenn kein Footer generiert werden konnte, gib den ursprünglichen Inhalt zurück
  if (!footerHtml) {
    console.warn('🦶 [FOOTER-GENERATOR] Could not generate footer. Using default footer.');
    
    // Erstelle einen einfachen Standard-Footer ohne nicht existierende Eigenschaften
    const defaultFooter = `
<!-- FOOTER -->
<div style="margin-top: 20px; border-top: 1px solid #eee; padding-top: 10px; color: #666; font-size: 12px;">
  ${userSettings?.footer_text ? `<p>${userSettings.footer_text}</p>` : '<p>BonusClub</p>'}
</div>
    `.trim();
    
    console.log(`🦶 [FOOTER-GENERATOR] Using default footer:`, defaultFooter);
    
    // Füge den Standard-Footer zum E-Mail-Inhalt hinzu
    const result = `${emailContent}\n\n${defaultFooter}`;
    console.log(`🦶 [FOOTER-GENERATOR] Final result with default footer:`, result?.substring(0, 500));
    return result;
  }
  
  // Füge den generierten Footer zum E-Mail-Inhalt hinzu
  const result = `${emailContent}\n\n${footerHtml}`;
  console.log(`🦶 [FOOTER-GENERATOR] Final result with generated footer:`, result?.substring(0, 500));
  return result;
};

/**
 * Konvertiert Plaintext in HTML mit Absätzen und Zeilenumbrüchen
 * @param {string} text - Der zu konvertierende Text
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers (optional)
 * @returns {string} HTML-formatierter Text
 */
export const convertToHtml = (text, userSettings = {}) => {
  if (!text) return '';
  
  // Schriftart aus den Einstellungen oder Standard verwenden
  const fontFamily = userSettings.footer_font || 'Helvetica Neue';
  
  // Ersetze Zeilenumbrüche durch <br> oder <p> Tags
  const withParagraphs = text
    .split('\n\n') // Leere Zeilen als Absatzgrenzen verwenden
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0)
    .map(paragraph => `<p style="font-family: '${fontFamily}', Helvetica, Arial, sans-serif;">${paragraph}</p>`)
    .join('');
    
  // Ersetze einzelne Zeilenumbrüche innerhalb von Absätzen durch <br>
  const withLineBreaks = withParagraphs.replace(/\n/g, '<br>');
  
  // Packe alles in ein div mit der Schriftart
  return `<div style="font-family: '${fontFamily}', Helvetica, Arial, sans-serif; color: #333; font-size: 14px; line-height: 1.6;">${withLineBreaks}</div>`;
}; 